import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Grid,
  TextField,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';

const defaultCareTeam = [
  { id: 'trial_management_team', label: 'Clinical Trial Management Team' },
  { id: 'trial_sponsor', label: 'Pharma Company / Trial Sponsor' },
  { id: 'community_support', label: 'Resource Providers / Community Health Partners' }
];

const trialBenefits = [
  'Personalized treatment planning with specialized trial oversight',
  'Access to investigational therapies and care coordination support',
  'Dedicated sponsor and care-network communication for participation readiness'
];

const requiredDocumentation = [
  'Government-issued ID and insurance card',
  'Pathology report and oncology history summary',
  'Current medication list and allergy history',
  'Signed informed consent packet'
];

const requiredTests = [
  'CBC / CMP baseline laboratory panel',
  'Biomarker panel or genetic profiling (as protocol requires)',
  'Baseline imaging (CT/MRI/PET as applicable)',
  'Cardiac and performance status evaluation if required by protocol'
];

const planningChecklist = [
  {
    title: 'Education & Understanding',
    items: [
      'Review trial purpose, phase, and treatment protocol with your care team',
      'Understand expected benefits, potential risks, and side effects',
      'Confirm schedule for visits, labs, imaging, and follow-ups'
    ]
  },
  {
    title: 'Clinical Preparation',
    items: [
      'Complete eligibility screening and baseline tests',
      'Organize prior records, pathology reports, and medication history',
      'Align comorbid condition management with trial requirements'
    ]
  },
  {
    title: 'Logistics & Support',
    items: [
      'Plan transportation, caregiving, and appointment support coverage',
      'Coordinate financial counseling, insurance authorizations, and grants',
      'Set communication plan with sponsor team and community partners'
    ]
  }
];

const resourceOrganizations = [
  'Florida Breast Care Coalition',
  'Breast Support Alliance',
  'Patient Navigation Network',
  'Community Wellness Partners',
  'Patient Ride Network',
  'Hope Housing Support Services'
];

const initialMessages = [
  {
    id: 'seed-1',
    to: ['Clinical Trial Management Team'],
    from: 'Patient',
    text: 'What documents should I prepare before the eligibility review?',
    timestamp: 'Today, 9:05 AM'
  },
  {
    id: 'seed-2',
    to: ['Patient'],
    from: 'Clinical Trial Management Team',
    text: 'Please bring your medication list, pathology report, and recent lab results.',
    timestamp: 'Today, 9:22 AM'
  }
];

const TrialParticipationPlanning = () => {
  const { trialId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const trialPlan = location.state?.trialPlan;
  const trialCode = trialPlan?.trialCode || trialId;
  const trialTitle = trialPlan?.title || 'Clinical Trial Participation Planning';
  const nextStep = trialPlan?.nextStep || 'Coordinate your care network and timeline for participation.';

  const [messages, setMessages] = useState(initialMessages);
  const [careTeamMembers, setCareTeamMembers] = useState(defaultCareTeam);
  const [selectedRecipients, setSelectedRecipients] = useState(['trial_management_team']);
  const [messageText, setMessageText] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [savedNotes, setSavedNotes] = useState('');
  const [resourceDialogOpen, setResourceDialogOpen] = useState(false);
  const [selectedResourceOrganizations, setSelectedResourceOrganizations] = useState([]);

  const toggleRecipient = (memberId) => {
    setSelectedRecipients((prev) => {
      if (prev.includes(memberId)) {
        return prev.filter((id) => id !== memberId);
      }

      return [...prev, memberId];
    });
  };

  const handleSaveNotes = () => {
    setSavedNotes(additionalNotes.trim());
  };

  const handleSendMessage = () => {
    const trimmed = messageText.trim();
    if (!trimmed || selectedRecipients.length === 0) {
      return;
    }

    const selectedRecipientLabels = careTeamMembers
      .filter((member) => selectedRecipients.includes(member.id))
      .map((member) => member.label);

    const newPatientMessage = {
      id: `patient-${Date.now()}`,
      to: selectedRecipientLabels,
      from: 'Patient',
      text: trimmed,
      timestamp: 'Just now'
    };

    const responseMessages = selectedRecipientLabels.map((label, index) => ({
      id: `response-${Date.now()}-${index}`,
      to: ['Patient'],
      from: label,
      text: `Thanks for your message. ${label} has received your question and will coordinate next steps for this trial plan.`,
      timestamp: 'Just now'
    }));

    setMessages(prev => [...prev, newPatientMessage, ...responseMessages]);
    setMessageText('');
  };

  const handleAddToDiscussionBoard = () => {
    setResourceDialogOpen(true);
  };

  const toggleResourceOrganization = (organization) => {
    setSelectedResourceOrganizations((prev) => {
      if (prev.includes(organization)) {
        return prev.filter((name) => name !== organization);
      }

      return [...prev, organization];
    });
  };

  const handleAddSelectedOrganizations = () => {
    if (selectedResourceOrganizations.length === 0) {
      return;
    }

    const addedMemberIds = selectedResourceOrganizations.map((organization) =>
      `resource_org_${organization.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`
    );

    setCareTeamMembers((prev) => {
      const next = [...prev];
      selectedResourceOrganizations.forEach((organization, index) => {
        const memberId = addedMemberIds[index];
        const label = `${organization} (Resource Organization)`;
        if (!next.some((member) => member.id === memberId)) {
          next.push({ id: memberId, label });
        }
      });
      return next;
    });

    setSelectedRecipients((prev) => Array.from(new Set([...prev, ...addedMemberIds])));

    setMessages((prev) => [
      ...prev,
      {
        id: `system-${Date.now()}`,
        to: ['Patient'],
        from: 'System',
        text: `Added to discussion board: ${selectedResourceOrganizations.join(', ')}.`,
        timestamp: 'Just now'
      }
    ]);

    setSelectedResourceOrganizations([]);
    setResourceDialogOpen(false);
  };

  const handleCloseResourceDialog = () => {
    setSelectedResourceOrganizations([]);
    setResourceDialogOpen(false);
  };

  const handleCreateDiscussionMessage = () => {
    const trimmed = messageText.trim();
    if (!trimmed || selectedRecipients.length === 0) {
      return;
    }

    const selectedRecipientLabels = careTeamMembers
      .filter((member) => selectedRecipients.includes(member.id))
      .map((member) => member.label);

    const discussionMessage = {
      id: `discussion-${Date.now()}`,
      to: selectedRecipientLabels,
      from: 'Patient',
      text: trimmed,
      timestamp: 'Just now'
    };

    setMessages(prev => [...prev, discussionMessage]);
    setMessageText('');
  };

  const sectionCardSx = {
    borderRadius: 3,
    border: 1,
    borderColor: 'divider',
    boxShadow: 0
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Button
        variant="text"
        sx={{ mb: 2, fontWeight: 600 }}
        onClick={() => navigate('/dashboard')}
      >
        ← Back to My Journey
      </Button>

      <Card
        sx={{
          ...sectionCardSx,
          mb: 3,
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.light}14, ${theme.palette.background.paper})`
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
            <Box>
          <Typography variant="h5" gutterBottom>
            Trial Participation Planning
          </Typography>
          <Typography variant="h6" color="primary.main" gutterBottom sx={{ fontWeight: 700 }}>
            {trialCode}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
            {trialTitle}
          </Typography>
          <Chip label={`Next Step: ${nextStep}`} color="primary" variant="filled" />
            </Box>
            <Button
              variant="contained"
              onClick={() => navigate(`/plans/plan-bc-2026`)}
            >
              Edit Plan
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/plans')}
            >
              My Plans
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/plans/new')}
            >
              Create Plan Wizard
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ ...sectionCardSx, height: '100%' }}>
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                Trial Benefits
              </Typography>
              <List dense>
                {trialBenefits.map((benefit, index) => (
                  <ListItem key={`benefit-${index}`} sx={{ px: 0 }}>
                    <ListItemText
                      primary={benefit}
                      primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ ...sectionCardSx, height: '100%' }}>
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                Documentation Needed
              </Typography>
              <List dense>
                {requiredDocumentation.map((document, index) => (
                  <ListItem key={`document-${index}`} sx={{ px: 0 }}>
                    <ListItemText
                      primary={document}
                      primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ ...sectionCardSx, height: '100%' }}>
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                Tests Needed
              </Typography>
              <List dense>
                {requiredTests.map((test, index) => (
                  <ListItem key={`test-${index}`} sx={{ px: 0 }}>
                    <ListItemText
                      primary={test}
                      primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ ...sectionCardSx, mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
            Additional Notes
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Add planning notes"
            placeholder="Capture personal reminders, concerns, or follow-up points..."
            value={additionalNotes}
            onChange={(event) => setAdditionalNotes(event.target.value)}
            sx={{ mt: 1 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="outlined" onClick={handleSaveNotes} disabled={!additionalNotes.trim()}>
              Save Notes
            </Button>
          </Box>
          {savedNotes && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Saved notes: {savedNotes}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card sx={{ ...sectionCardSx, mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
            Clinical Trial Planning Checklist
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Planning includes educational readiness, clinical preparation, and coordination with providers, healthcare teams, sponsors, and community partners.
          </Typography>
          <Grid container spacing={2}>
            {planningChecklist.map((section) => (
              <Grid item xs={12} md={4} key={section.title}>
                <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      {section.title}
                    </Typography>
                    <List dense>
                      {section.items.map((item, index) => (
                        <ListItem key={`${section.title}-${index}`} sx={{ px: 0 }}>
                          <ListItemText
                            primary={item}
                            primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ ...sectionCardSx }}>
        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
            Planning Discussion Board
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send messages to your trial care network and track responses in one list.
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Send To (select one or more)
            </Typography>
            <FormGroup sx={{ pl: 0.5 }}>
              {careTeamMembers.map((member) => (
                <FormControlLabel
                  key={member.id}
                  control={
                    <Checkbox
                      checked={selectedRecipients.includes(member.id)}
                      onChange={() => toggleRecipient(member.id)}
                    />
                  }
                  label={member.label}
                />
              ))}
            </FormGroup>
          </Box>

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Type your message"
            placeholder="Ask your question about participation planning..."
            value={messageText}
            onChange={(event) => setMessageText(event.target.value)}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1, mt: 2, mb: 2 }}>
            <Button
              variant="outlined"
              onClick={handleAddToDiscussionBoard}
            >
              Add to Discussion Board
            </Button>
            <Button
              variant="outlined"
              onClick={handleCreateDiscussionMessage}
              disabled={!messageText.trim() || selectedRecipients.length === 0}
            >
              Post to Board
            </Button>
            <Button variant="contained" onClick={handleSendMessage} disabled={!messageText.trim() || selectedRecipients.length === 0}>
              Send Message
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          <List
            sx={{
              maxHeight: 420,
              overflowY: 'auto',
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              p: 1,
              bgcolor: 'background.default'
            }}
          >
            {messages.map((message) => (
              <ListItem
                key={message.id}
                alignItems="flex-start"
                sx={{
                  px: 1,
                  py: 1,
                  mb: 1,
                  borderRadius: 1.5,
                  bgcolor: message.from === 'Patient' ? 'primary.light' : 'background.paper'
                }}
              >
                <ListItemText
                  primary={`${message.from} → ${message.to.join(', ')}`}
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                  secondary={
                    <>
                      <Typography component="span" variant="body2" color="text.primary" sx={{ display: 'block', mb: 0.75 }}>
                        {message.text}
                      </Typography>
                      <Typography component="span" variant="caption" color="text.secondary">
                        {message.timestamp}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      <Dialog open={resourceDialogOpen} onClose={handleCloseResourceDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Select Resource Organizations</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose organizations from the resources list to add them to the discussion board recipients.
          </Typography>
          <FormGroup>
            {resourceOrganizations.map((organization) => (
              <FormControlLabel
                key={organization}
                control={
                  <Checkbox
                    checked={selectedResourceOrganizations.includes(organization)}
                    onChange={() => toggleResourceOrganization(organization)}
                  />
                }
                label={organization}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResourceDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddSelectedOrganizations}
            disabled={selectedResourceOrganizations.length === 0}
          >
            Add Selected
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrialParticipationPlanning;
